import { useState } from "react"
import { TiptapEditor, TiptapReadOnlyContent } from "../../component/tiptapEditor"
import { PreviewTabType, ProcessedQuestionContent } from "../QuestionPreview/types"

export const PREVIEW_TABS: { key: PreviewTabType; label: string }[] = [
  { key: "question_result", label: "question" },
  { key: "solution_result", label: "solution" },
  { key: "answer_result", label: "answer" },
  { key: "marking_scheme", label: "marking scheme" },
]

const TAB_FIELD_MAP: Record<PreviewTabType, keyof ProcessedQuestionContent> = {
  question_result: "question",
  solution_result: "solution",
  answer_result: "answer",
  marking_scheme: "marking_scheme",
}

const TAB_EDITOR_CONFIG: Record<
  PreviewTabType,
  { label: string; placeholder: string; height: number }
> = {
  question_result: {
    label: "Question",
    placeholder: "Enter the question…",
    height: 360,
  },
  solution_result: {
    label: "Full solution",
    placeholder: "Enter the full solution…",
    height: 280,
  },
  answer_result: {
    label: "Answer",
    placeholder: "Enter the answer…",
    height: 220,
  },
  marking_scheme: {
    label: "Marking scheme",
    placeholder: "Describe how marks are awarded…",
    height: 220,
  },
}

export const getPreviewTabContent = (
  content: ProcessedQuestionContent,
  tab: PreviewTabType
) => {
  const field = TAB_FIELD_MAP[tab]
  return (content[field] as string) || ""
}

type ContentTabsProps = {
  activeTab: PreviewTabType
  onTabChange: (tab: PreviewTabType) => void
  className?: string
}

export const ContentTabs = ({
  activeTab,
  onTabChange,
  className = "question_preview_v2__tabs",
}: ContentTabsProps) => (
  <div className={className} role='tablist' aria-label='Content sections'>
    {PREVIEW_TABS.map(({ key, label }) => (
      <button
        key={key}
        type='button'
        role='tab'
        aria-selected={activeTab === key}
        className={`question_preview_v2__tab ${activeTab === key ? "question_preview_v2__tab--active" : ""}`}
        onClick={() => onTabChange(key)}
      >
        {label}
      </button>
    ))}
  </div>
)

type ContentPreviewV2Props = {
  content: ProcessedQuestionContent
  activeTab: PreviewTabType
}

export const ContentPreviewV2 = ({
  content,
  activeTab,
}: ContentPreviewV2Props) => {
  const html = getPreviewTabContent(content, activeTab) || "<p></p>"

  return (
    <div className='question_content question_content--preview'>
      <TiptapReadOnlyContent html={html} />
    </div>
  )
}

type ContentEditV2Props = {
  cardIndex: number
  content: ProcessedQuestionContent
  onChange: (name: string, value: string) => void
  questionError?: string | null
  activeTab?: PreviewTabType
  onTabChange?: (tab: PreviewTabType) => void
}

export const ContentEditV2 = ({
  cardIndex,
  content,
  onChange,
  questionError,
  activeTab: controlledTab,
  onTabChange,
}: ContentEditV2Props) => {
  const [internalTab, setInternalTab] =
    useState<PreviewTabType>("question_result")

  const activeTab = controlledTab ?? internalTab
  const setActiveTab = onTabChange ?? setInternalTab

  const field = TAB_FIELD_MAP[activeTab]
  const editorConfig = TAB_EDITOR_CONFIG[activeTab]
  const value = (content[field] as string) || ""

  return (
    <div className='question_preview_v2__edit-panel'>
      <ContentTabs activeTab={activeTab} onTabChange={setActiveTab} />

      <div className='question_preview_v2__editors'>
        <div className='textEditor_container'>
          <TiptapEditor
            key={`${cardIndex}-${activeTab}`}
            value={value}
            id={`preview_${cardIndex}_${activeTab}`}
            handleOnChange={(nextValue) => onChange(field, nextValue)}
            label={editorConfig.label}
            height={editorConfig.height}
            placeholder={editorConfig.placeholder}
          />
          {activeTab === "question_result" && questionError && (
            <p className='field-error'>{questionError}</p>
          )}
        </div>
      </div>
    </div>
  )
}
